import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import './DatePicker.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DatePicker({
    label,
    value,
    onChange,
    placeholder = 'Select date',
    minDate,
    maxDate,
    error,
    disabled = false,
    required = false,
    className = ''
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(value ? new Date(value) : new Date());
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };

    const handlePrevMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const handleSelectDate = (day) => {
        const selected = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        onChange?.(selected.toISOString().split('T')[0]);
        setIsOpen(false);
    };

    const isDateDisabled = (day) => {
        const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        if (minDate && date < new Date(minDate)) return true;
        if (maxDate && date > new Date(maxDate)) return true;
        return false;
    };

    const isToday = (day) => {
        const today = new Date();
        return viewDate.getFullYear() === today.getFullYear() &&
            viewDate.getMonth() === today.getMonth() &&
            day === today.getDate();
    };

    const isSelected = (day) => {
        if (!value) return false;
        const selected = new Date(value);
        return viewDate.getFullYear() === selected.getFullYear() &&
            viewDate.getMonth() === selected.getMonth() &&
            day === selected.getDate();
    };

    const renderDays = () => {
        const daysInMonth = getDaysInMonth(viewDate.getFullYear(), viewDate.getMonth());
        const firstDay = getFirstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth());
        const days = [];

        // Empty cells for days before first day
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="datepicker-day empty" />);
        }

        // Days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const disabled = isDateDisabled(day);
            days.push(
                <button
                    key={day}
                    type="button"
                    className={`datepicker-day 
                        ${isToday(day) ? 'today' : ''} 
                        ${isSelected(day) ? 'selected' : ''}
                        ${disabled ? 'disabled' : ''}`}
                    onClick={() => !disabled && handleSelectDate(day)}
                    disabled={disabled}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    return (
        <div className={`datepicker-wrapper ${className}`} ref={containerRef}>
            {label && (
                <label className="datepicker-label">
                    {label}
                    {required && <span className="datepicker-required">*</span>}
                </label>
            )}

            <div
                className={`datepicker-input ${isOpen ? 'open' : ''} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <Calendar size={18} className="datepicker-icon" />
                <span className={value ? '' : 'placeholder'}>
                    {value ? formatDate(value) : placeholder}
                </span>
            </div>

            {isOpen && (
                <div className="datepicker-dropdown">
                    <div className="datepicker-header">
                        <button type="button" onClick={handlePrevMonth}>
                            <ChevronLeft size={18} />
                        </button>
                        <span>{MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}</span>
                        <button type="button" onClick={handleNextMonth}>
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="datepicker-weekdays">
                        {DAYS.map(day => (
                            <div key={day} className="datepicker-weekday">{day}</div>
                        ))}
                    </div>

                    <div className="datepicker-days">
                        {renderDays()}
                    </div>
                </div>
            )}

            {error && <span className="datepicker-error">{error}</span>}
        </div>
    );
}

DatePicker.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    error: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    className: PropTypes.string
};
